import {
  ArrowRight,
  BarChart3,
  CalendarDays,
  Clock3,
  ExternalLink,
  FileText,
  Layers3,
  ListTodo,
  PencilLine,
  Plus,
  Search,
  Sparkles,
  Target,
  Tags,
  Trash2,
  UserRound,
  Workflow,
} from "lucide-react";
import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { TRUEVANCE_BRAND_NAME, TruevanceBrandHeader, useTruevanceBranding } from "./TruevanceBranding";
import { formatCurrency, formatDisplayDate } from "./TruevanceConnectData";
import {
  archiveTruevanceDiscoveryCall,
  archiveTruevanceLeadSource,
  archiveTruevanceLeadTag,
  archiveTruevanceSalesFollowUp,
  archiveTruevanceSalesLead,
  archiveTruevanceSalesOpportunity,
  createTruevanceDiscoveryCall,
  createTruevanceLeadSource,
  createTruevanceLeadTag,
  createTruevanceSalesFollowUp,
  createTruevanceSalesLead,
  createTruevanceSalesOpportunity,
  loadTruevanceSalesWorkspace,
  setTruevanceLeadTags,
  updateTruevanceDiscoveryCall,
  updateTruevanceLeadSource,
  updateTruevanceLeadTag,
  updateTruevanceSalesFollowUp,
  updateTruevanceSalesLead,
  updateTruevanceSalesOpportunity,
} from "./TruevanceSalesCenterApi";
import type {
  BusinessDiscoveryCallRecord,
  BusinessFollowUpRecord,
  BusinessLeadActivityRecord,
  BusinessLeadRecord,
  BusinessLeadSourceRecord,
  BusinessLeadStatusHistoryRecord,
  BusinessLeadTagRecord,
  BusinessSalesWorkspacePayload,
  SalesDiscoveryCallInput,
  SalesFollowUpInput,
  SalesLeadInput,
  SalesOpportunityInput,
} from "./TruevanceSalesCenterData";

type ScopeOption = {
  value: string;
  label: string;
  count: number;
};

const emptyLeadForm = (): SalesLeadInput => ({
  companyId: "",
  workspaceId: "",
  leadSourceId: "",
  leadOwner: "",
  assignedUserId: "",
  teamAssignment: "",
  leadPriority: "Medium",
  productFamily: "",
  productKey: "",
  inquirySource: "Website Inquiry",
  currentStage: "Lead Intake",
  status: "New Inquiry",
});

const emptyOpportunityForm = (): SalesOpportunityInput => ({
  leadId: "",
  opportunityType: "Business Consulting",
  currentStage: "Opportunity Management",
  status: "Active",
  leadPriority: "Medium",
});

const emptyDiscoveryForm = (): SalesDiscoveryCallInput => ({
  leadId: "",
  scheduledAt: toDateTimeInputValue(new Date()),
  currentStage: "Discovery Scheduled",
  status: "Scheduled",
  leadPriority: "Medium",
});

const emptyFollowUpForm = (): SalesFollowUpInput => ({
  entityType: "lead",
  entityId: "",
  leadId: null,
  dueDate: new Date().toISOString().slice(0, 10),
  priority: "Medium",
  status: "Open",
  leadPriority: "Medium",
});

const emptySourceForm = (): {
  sourceName: string;
  sourceGroup: string;
  description: string;
  companyId: string;
  workspaceId: string;
  sortOrder: number;
} => ({
  sourceName: "",
  sourceGroup: "Lead Intake",
  description: "",
  companyId: "",
  workspaceId: "",
  sortOrder: 0,
});

const emptyTagForm = (): {
  tagName: string;
  tagColor: string;
  description: string;
  companyId: string;
  workspaceId: string;
  sortOrder: number;
} => ({
  tagName: "",
  tagColor: "#17D4C4",
  description: "",
  companyId: "",
  workspaceId: "",
  sortOrder: 0,
});

export function TruevanceSalesCenterPage() {
  const [workspace, setWorkspace] = useState<BusinessSalesWorkspacePayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [companyFilter, setCompanyFilter] = useState("all");
  const [workspaceFilter, setWorkspaceFilter] = useState("all");
  const [selectedLeadId, setSelectedLeadId] = useState("");
  const [leadTagDraftIds, setLeadTagDraftIds] = useState<string[]>([]);

  const [leadForm, setLeadForm] = useState<SalesLeadInput>(emptyLeadForm());
  const [leadEditingId, setLeadEditingId] = useState("");
  const [opportunityForm, setOpportunityForm] = useState<SalesOpportunityInput>(emptyOpportunityForm());
  const [opportunityEditingId, setOpportunityEditingId] = useState("");
  const [discoveryForm, setDiscoveryForm] = useState<SalesDiscoveryCallInput>(emptyDiscoveryForm());
  const [discoveryEditingId, setDiscoveryEditingId] = useState("");
  const [followUpForm, setFollowUpForm] = useState<SalesFollowUpInput>(emptyFollowUpForm());
  const [followUpEditingId, setFollowUpEditingId] = useState("");
  const [sourceForm, setSourceForm] = useState(emptySourceForm());
  const [sourceEditingId, setSourceEditingId] = useState("");
  const [tagForm, setTagForm] = useState(emptyTagForm());
  const [tagEditingId, setTagEditingId] = useState("");

  useTruevanceBranding(
    `${TRUEVANCE_BRAND_NAME} | Sales & Lead Center`,
    "Shared CRM, assignment, discovery, and follow-up workspace for all Business Center OS tenants.",
  );

  const loadWorkspace = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await loadTruevanceSalesWorkspace();
      setWorkspace(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to load the Sales & Lead Center workspace.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadWorkspace();
  }, []);

  const scopeOptions = useMemo(() => buildScopeOptions(workspace), [workspace]);
  const leadSourceOptions = workspace?.leadSources || [];
  const leadTagOptions = workspace?.leadTags || [];

  useEffect(() => {
    if (!workspace) return;
    const firstCompany = scopeOptions.companies[0]?.value || "";
    const firstWorkspace = scopeOptions.workspaces[0]?.value || "";
    setLeadForm((current) => ({
      ...current,
      companyId: current.companyId || firstCompany,
      workspaceId: current.workspaceId || firstWorkspace,
    }));
    setSourceForm((current) => ({
      ...current,
      companyId: current.companyId || firstCompany,
      workspaceId: current.workspaceId || firstWorkspace,
    }));
    setTagForm((current) => ({
      ...current,
      companyId: current.companyId || firstCompany,
      workspaceId: current.workspaceId || firstWorkspace,
    }));
  }, [workspace, scopeOptions.companies, scopeOptions.workspaces]);

  const filteredLeads = useMemo(
    () =>
      filterLeads(workspace?.leads || [], {
        companyId: companyFilter,
        workspaceId: workspaceFilter,
        searchTerm,
      }),
    [companyFilter, searchTerm, workspace?.leads, workspaceFilter],
  );

  const filteredOpportunities = useMemo(
    () =>
      filterByScope(workspace?.opportunities || [], {
        companyId: companyFilter,
        workspaceId: workspaceFilter,
        searchTerm,
      }),
    [companyFilter, searchTerm, workspace?.opportunities, workspaceFilter],
  );

  const filteredDiscoveryCalls = useMemo(
    () =>
      filterByScope(workspace?.discoveryCalls || [], {
        companyId: companyFilter,
        workspaceId: workspaceFilter,
        searchTerm,
      }),
    [companyFilter, searchTerm, workspace?.discoveryCalls, workspaceFilter],
  );

  const filteredFollowUps = useMemo(
    () =>
      filterByScope(workspace?.followUps || [], {
        companyId: companyFilter,
        workspaceId: workspaceFilter,
        searchTerm,
      }),
    [companyFilter, searchTerm, workspace?.followUps, workspaceFilter],
  );

  const selectedLead = useMemo(() => {
    if (!workspace) return null;
    return workspace.leads.find((lead) => lead.id === selectedLeadId) || filteredLeads[0] || null;
  }, [filteredLeads, selectedLeadId, workspace]);

  useEffect(() => {
    if (!selectedLead && filteredLeads[0]) {
      setSelectedLeadId(filteredLeads[0].id);
    }
  }, [filteredLeads, selectedLead]);

  useEffect(() => {
    setLeadTagDraftIds(selectedLead?.tagIds || []);
  }, [selectedLead?.id, selectedLead?.tagIds?.join("|")]);

  const selectedLeadStatusHistory = useMemo(() => {
    if (!workspace) return [];
    const items = workspace.leadStatusHistory.filter((item) => (selectedLead ? item.leadId === selectedLead.id : true));
    return items.slice(0, 16);
  }, [selectedLead, workspace]);

  const selectedLeadActivities = useMemo(() => {
    if (!workspace) return [];
    const items = workspace.leadActivities.filter((item) => (selectedLead ? item.leadId === selectedLead.id : true));
    return items.slice(0, 16);
  }, [selectedLead, workspace]);

  const selectedLeadTimeline = selectedLead?.timelineEvents || [];

  const metrics = useMemo(() => {
    if (!workspace) {
      return {
        leads: 0,
        opportunities: 0,
        discoveryCalls: 0,
        followUps: 0,
        dueFollowUps: 0,
        sources: 0,
        tags: 0,
      };
    }
    return {
      leads: filteredLeads.length,
      opportunities: filteredOpportunities.length,
      discoveryCalls: filteredDiscoveryCalls.length,
      followUps: filteredFollowUps.length,
      dueFollowUps: filteredFollowUps.filter((item) => item.status !== "Complete" && item.status !== "Archived" && item.dueDate <= new Date().toISOString().slice(0, 10)).length,
      sources: leadSourceOptions.length,
      tags: leadTagOptions.length,
    };
  }, [filteredDiscoveryCalls.length, filteredFollowUps, filteredLeads.length, filteredOpportunities.length, leadSourceOptions.length, leadTagOptions.length, workspace]);

  const handleLeadSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    try {
      let savedLead: BusinessLeadRecord | null = null;
      if (leadEditingId) {
        const response = await updateTruevanceSalesLead(leadEditingId, leadForm);
        savedLead = response.lead;
        if (leadTagDraftIds.length) {
          await setTruevanceLeadTags(leadEditingId, leadTagDraftIds);
        }
      } else {
        const response = await createTruevanceSalesLead(leadForm);
        savedLead = response.lead;
      }
      setLeadEditingId("");
      setLeadForm(emptyLeadForm());
      setLeadTagDraftIds([]);
      if (savedLead) {
        setSelectedLeadId(savedLead.id);
      }
      await loadWorkspace();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to save lead.");
    }
  };

  const handleOpportunitySubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    try {
      if (!opportunityForm.leadId) {
        setError("Select a lead before saving an opportunity.");
        return;
      }
      if (opportunityEditingId) {
        await updateTruevanceSalesOpportunity(opportunityEditingId, opportunityForm);
      } else {
        await createTruevanceSalesOpportunity(opportunityForm);
      }
      setOpportunityEditingId("");
      setOpportunityForm(emptyOpportunityForm());
      await loadWorkspace();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to save opportunity.");
    }
  };

  const handleDiscoverySubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    try {
      if (!discoveryForm.leadId) {
        setError("Select a lead before saving a discovery call.");
        return;
      }
      if (discoveryEditingId) {
        await updateTruevanceDiscoveryCall(discoveryEditingId, discoveryForm);
      } else {
        await createTruevanceDiscoveryCall(discoveryForm);
      }
      setDiscoveryEditingId("");
      setDiscoveryForm(emptyDiscoveryForm());
      await loadWorkspace();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to save discovery call.");
    }
  };

  const handleFollowUpSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    try {
      if (!followUpForm.entityId) {
        setError("Enter an entity id before saving a follow-up.");
        return;
      }
      if (followUpEditingId) {
        await updateTruevanceSalesFollowUp(followUpEditingId, followUpForm);
      } else {
        await createTruevanceSalesFollowUp(followUpForm);
      }
      setFollowUpEditingId("");
      setFollowUpForm(emptyFollowUpForm());
      await loadWorkspace();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to save follow-up.");
    }
  };

  const handleSourceSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    try {
      if (sourceEditingId) {
        await updateTruevanceLeadSource(sourceEditingId, sourceForm);
      } else {
        await createTruevanceLeadSource(sourceForm);
      }
      setSourceEditingId("");
      setSourceForm(emptySourceForm());
      await loadWorkspace();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to save lead source.");
    }
  };

  const handleTagSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    try {
      if (tagEditingId) {
        await updateTruevanceLeadTag(tagEditingId, tagForm);
      } else {
        await createTruevanceLeadTag(tagForm);
      }
      setTagEditingId("");
      setTagForm(emptyTagForm());
      await loadWorkspace();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to save lead tag.");
    }
  };

  const handleSelectLead = (lead: BusinessLeadRecord) => {
    setSelectedLeadId(lead.id);
    setLeadEditingId(lead.id);
    setLeadForm({
      organizationId: lead.organizationId || null,
      contactId: lead.contactId || null,
      companyName: lead.organizationName || "",
      contactName: lead.contactName || lead.fullName || "",
      email: lead.email || "",
      phone: lead.phone || "",
      inquirySource: lead.inquirySource || "Website Inquiry",
      companyId: lead.companyId || "",
      workspaceId: lead.workspaceId || "",
      leadSourceId: lead.leadSourceId || "",
      leadOwner: lead.leadOwner || "",
      assignedUserId: lead.assignedUserId || "",
      teamAssignment: lead.teamAssignment || "",
      leadPriority: lead.leadPriority || "Medium",
      productFamily: lead.productFamily || "",
      productKey: lead.productKey || "",
      eventType: lead.eventType || "",
      eventDate: lead.eventDate || "",
      serviceInterest: lead.serviceInterest || "",
      budget: lead.budget || "",
      timeline: lead.timeline || "",
      currentStage: lead.currentStage || "Lead Intake",
      status: lead.status || "New Inquiry",
      assignedOwner: lead.assignedOwner || "",
      nextAction: lead.nextAction || "",
      followUpDate: lead.followUpDate || "",
      notes: lead.notes || "",
    });
    setLeadTagDraftIds(lead.tagIds || []);
  };

  if (loading && !workspace) {
    return (
      <main className="truevance-shell truevance-business-shell min-h-screen text-[#F5F7FA]">
        <TruevanceBrandHeader />
        <section className="px-4 py-12 sm:px-6 lg:px-8">
          <div className="mx-auto grid max-w-7xl gap-6">
            <div className="truevance-business-loading grid gap-4 rounded-3xl p-8">
              <p className="text-xs font-black uppercase tracking-[0.22em] text-[#17D4C4]">{TRUEVANCE_BRAND_NAME}</p>
              <h1 className="font-display text-4xl font-bold text-[#071A2F]">Sales &amp; Lead Center</h1>
              <p className="text-sm font-semibold leading-8 text-[#334155]">Loading shared CRM records, assignments, source catalogs, and activity history.</p>
            </div>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className="truevance-shell truevance-business-shell min-h-screen text-[#F5F7FA]">
      <TruevanceBrandHeader />

      <section className="truevance-business-hero border-b border-[rgba(34,224,208,.16)] px-4 py-12 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-7xl gap-6 lg:grid-cols-[1.2fr_.8fr] lg:items-end">
          <div className="grid gap-4">
            <div className="inline-flex w-fit items-center gap-2 rounded-full border border-[rgba(34,224,208,.32)] bg-[rgba(7,26,47,.55)] px-3 py-2 text-xs font-black uppercase tracking-[0.22em] text-[#22E0D0]">
              <Sparkles className="size-4" aria-hidden="true" />
              Shared CRM across all workspaces
            </div>
            <h1 className="max-w-4xl font-display text-5xl font-bold leading-tight sm:text-6xl">Sales &amp; Lead Center</h1>
            <p className="max-w-3xl text-base font-semibold leading-8 text-[#E8EEF4]">
              Manage lead intake, discovery, opportunities, follow-ups, source catalogs, tag catalogs, and assignment history from one workspace-aware command center.
            </p>
          </div>

          <article className="truevance-business-card truevance-business-card--dark rounded-2xl p-5 sm:p-6">
            <p className="text-xs font-black uppercase tracking-[0.18em] text-[#22E0D0]">Operational scope</p>
            <h2 className="mt-2 font-display text-3xl font-bold text-[#F5F7FA]">One CRM. Many workspaces.</h2>
            <p className="mt-3 text-sm font-semibold leading-7 text-[#E8EEF4]">
              Leads remain associated to company and workspace context throughout the lifecycle so the same structure can support Business Center OS workspaces and future tenants.
            </p>
            <div className="mt-5 flex flex-wrap gap-2">
              <Link to="/admin/truevance-business-center" className="admin-primary-button focus-ring">
                Business Center Home
              </Link>
              <Link to="/admin/executive-dashboard" className="admin-secondary-button focus-ring">
                Executive Dashboard
              </Link>
            </div>
          </article>
        </div>
      </section>

      <section className="px-4 py-10 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-7xl gap-8">
          {error ? (
            <section className="truevance-business-card truevance-business-card--light rounded-2xl border border-[rgba(214,178,74,.2)] p-4 text-[#071A2F]">
              <p className="text-sm font-bold">Sales &amp; Lead Center notice</p>
              <p className="mt-2 text-sm font-semibold text-[#334155]">{error}</p>
              <button type="button" className="admin-secondary-button focus-ring mt-4" onClick={() => setError("")}>
                Dismiss
              </button>
            </section>
          ) : null}

          <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <MetricCard label="Active Leads" value={metrics.leads} note="Filtered by scope and search." tone="dark" />
            <MetricCard label="Opportunities" value={metrics.opportunities} note="Shared pipeline records." tone="light" />
            <MetricCard label="Follow-Ups Due" value={metrics.dueFollowUps} note="Open items at or past due." tone="dark" />
            <MetricCard label="Sources / Tags" value={`${metrics.sources} / ${metrics.tags}`} note="Reusable lead classification catalogs." tone="light" />
          </section>

          <section className="truevance-business-card truevance-business-card--light rounded-2xl p-5 sm:p-6">
            <SectionHeading
              title="Workspace filter and assignment scope"
              subtitle="Switch between company and workspace contexts without leaving the shared CRM surface."
            />
            <div className="mt-5 grid gap-4 lg:grid-cols-4">
              <Field label="Search">
                <div className="relative">
                  <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-[#7A8AA0]" aria-hidden="true" />
                  <input
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                    className="admin-input pl-9"
                    placeholder="Search leads, sources, tags, or notes"
                  />
                </div>
              </Field>
              <Field label="Company filter">
                <select value={companyFilter} onChange={(event) => setCompanyFilter(event.target.value)} className="admin-input">
                  <option value="all">All companies</option>
                  {scopeOptions.companies.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label} ({option.count})
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Workspace filter">
                <select value={workspaceFilter} onChange={(event) => setWorkspaceFilter(event.target.value)} className="admin-input">
                  <option value="all">All workspaces</option>
                  {scopeOptions.workspaces.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label} ({option.count})
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Current selection">
                <div className="admin-input flex min-h-11 items-center gap-2 overflow-hidden text-sm font-semibold text-[#334155]">
                  <span className="rounded-full bg-[rgba(34,224,208,.12)] px-3 py-1 text-xs font-black uppercase tracking-[0.14em] text-[#071A2F]">
                    {selectedLead ? selectedLead.leadNumber : "No lead selected"}
                  </span>
                  <span className="truncate">{selectedLead?.fullName || selectedLead?.organizationName || "Select a lead to inspect the full lifecycle."}</span>
                </div>
              </Field>
            </div>
          </section>

          <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <ScopePill title="Companies" value={scopeOptions.companies.length} description="Shared parent-company scope surfaced in the CRM." />
            <ScopePill title="Workspaces" value={scopeOptions.workspaces.length} description="Workspace-aware filtering and assignment." />
            <ScopePill title="Selected lead" value={selectedLead?.status || "None"} description={selectedLead?.currentStage || "No lead selected yet."} />
            <ScopePill title="Timeline entries" value={selectedLeadTimeline.length} description="Latest lifecycle activity for the selected lead." />
          </section>

          <section className="grid gap-6 xl:grid-cols-[1.05fr_.95fr]">
            <article className="truevance-business-card truevance-business-card--light rounded-2xl p-5 sm:p-6">
              <SectionHeading
                title={leadEditingId ? "Update lead and assignment" : "Lead management"}
                subtitle="Create or edit the shared lead record, then assign company scope, workspace scope, owner, user, and tags."
              />
              <form className="mt-6 grid gap-4" onSubmit={handleLeadSubmit}>
                <div className="grid gap-4 md:grid-cols-2">
                  <Field label="Company">
                    <select
                      value={leadForm.companyId || ""}
                      onChange={(event) => setLeadForm((state) => ({ ...state, companyId: event.target.value }))}
                      className="admin-input"
                    >
                      <option value="">Select company</option>
                      {scopeOptions.companies.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Workspace">
                    <select
                      value={leadForm.workspaceId || ""}
                      onChange={(event) => setLeadForm((state) => ({ ...state, workspaceId: event.target.value }))}
                      className="admin-input"
                    >
                      <option value="">Select workspace</option>
                      {scopeOptions.workspaces.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Company name">
                    <input value={leadForm.companyName || ""} onChange={(event) => setLeadForm((state) => ({ ...state, companyName: event.target.value }))} className="admin-input" />
                  </Field>
                  <Field label="Contact name">
                    <input value={leadForm.contactName || ""} onChange={(event) => setLeadForm((state) => ({ ...state, contactName: event.target.value }))} className="admin-input" />
                  </Field>
                  <Field label="Email">
                    <input type="email" value={leadForm.email || ""} onChange={(event) => setLeadForm((state) => ({ ...state, email: event.target.value }))} className="admin-input" />
                  </Field>
                  <Field label="Phone">
                    <input value={leadForm.phone || ""} onChange={(event) => setLeadForm((state) => ({ ...state, phone: event.target.value }))} className="admin-input" />
                  </Field>
                  <Field label="Lead source">
                    <select
                      value={leadForm.leadSourceId || ""}
                      onChange={(event) => setLeadForm((state) => ({ ...state, leadSourceId: event.target.value }))}
                      className="admin-input"
                    >
                      <option value="">Select source</option>
                      {leadSourceOptions.map((source) => (
                        <option key={source.id} value={source.id}>
                          {source.sourceName}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Inquiry source">
                    <input value={leadForm.inquirySource || ""} onChange={(event) => setLeadForm((state) => ({ ...state, inquirySource: event.target.value }))} className="admin-input" />
                  </Field>
                  <Field label="Lead owner">
                    <input value={leadForm.leadOwner || ""} onChange={(event) => setLeadForm((state) => ({ ...state, leadOwner: event.target.value }))} className="admin-input" />
                  </Field>
                  <Field label="Assigned user">
                    <input value={leadForm.assignedUserId || ""} onChange={(event) => setLeadForm((state) => ({ ...state, assignedUserId: event.target.value }))} className="admin-input" />
                  </Field>
                  <Field label="Team assignment">
                    <input value={leadForm.teamAssignment || ""} onChange={(event) => setLeadForm((state) => ({ ...state, teamAssignment: event.target.value }))} className="admin-input" />
                  </Field>
                  <Field label="Priority">
                    <select value={leadForm.leadPriority || "Medium"} onChange={(event) => setLeadForm((state) => ({ ...state, leadPriority: event.target.value }))} className="admin-input">
                      {["Critical", "High", "Medium", "Low"].map((priority) => (
                        <option key={priority} value={priority}>
                          {priority}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Product family">
                    <input value={leadForm.productFamily || ""} onChange={(event) => setLeadForm((state) => ({ ...state, productFamily: event.target.value }))} className="admin-input" />
                  </Field>
                  <Field label="Product key">
                    <input value={leadForm.productKey || ""} onChange={(event) => setLeadForm((state) => ({ ...state, productKey: event.target.value }))} className="admin-input" />
                  </Field>
                  <Field label="Event type">
                    <input value={leadForm.eventType || ""} onChange={(event) => setLeadForm((state) => ({ ...state, eventType: event.target.value }))} className="admin-input" />
                  </Field>
                  <Field label="Event date">
                    <input value={leadForm.eventDate || ""} onChange={(event) => setLeadForm((state) => ({ ...state, eventDate: event.target.value }))} className="admin-input" />
                  </Field>
                  <Field label="Service interest">
                    <input value={leadForm.serviceInterest || ""} onChange={(event) => setLeadForm((state) => ({ ...state, serviceInterest: event.target.value }))} className="admin-input" />
                  </Field>
                  <Field label="Budget">
                    <input value={leadForm.budget || ""} onChange={(event) => setLeadForm((state) => ({ ...state, budget: event.target.value }))} className="admin-input" />
                  </Field>
                  <Field label="Timeline">
                    <input value={leadForm.timeline || ""} onChange={(event) => setLeadForm((state) => ({ ...state, timeline: event.target.value }))} className="admin-input" />
                  </Field>
                  <Field label="Current stage">
                    <input value={leadForm.currentStage || ""} onChange={(event) => setLeadForm((state) => ({ ...state, currentStage: event.target.value }))} className="admin-input" />
                  </Field>
                  <Field label="Status">
                    <input value={leadForm.status || ""} onChange={(event) => setLeadForm((state) => ({ ...state, status: event.target.value }))} className="admin-input" />
                  </Field>
                  <Field label="Assigned owner">
                    <input value={leadForm.assignedOwner || ""} onChange={(event) => setLeadForm((state) => ({ ...state, assignedOwner: event.target.value }))} className="admin-input" />
                  </Field>
                  <Field label="Follow-up date">
                    <input type="date" value={leadForm.followUpDate || ""} onChange={(event) => setLeadForm((state) => ({ ...state, followUpDate: event.target.value }))} className="admin-input" />
                  </Field>
                </div>
                <Field label="Notes">
                  <textarea rows={4} value={leadForm.notes || ""} onChange={(event) => setLeadForm((state) => ({ ...state, notes: event.target.value }))} className="admin-input" />
                </Field>

                {leadEditingId ? (
                  <div className="rounded-2xl border border-[rgba(7,26,47,.08)] bg-[rgba(245,247,250,.88)] p-4">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="text-xs font-black uppercase tracking-[0.18em] text-[#17D4C4]">Lead tags</p>
                        <h3 className="font-display text-2xl font-bold text-[#071A2F]">Assignment tags</h3>
                      </div>
                      <button
                        type="button"
                        className="admin-secondary-button focus-ring"
                        onClick={async () => {
                          await setTruevanceLeadTags(leadEditingId, leadTagDraftIds);
                          await loadWorkspace();
                        }}
                      >
                        <Tags className="size-4" aria-hidden="true" />
                        Save tags
                      </button>
                    </div>
                    <div className="mt-4 grid gap-3 md:grid-cols-2">
                      {leadTagOptions.length ? (
                        leadTagOptions.map((tag) => {
                          const checked = leadTagDraftIds.includes(tag.id);
                          return (
                            <label
                              key={tag.id}
                              className={`flex items-start gap-3 rounded-2xl border p-3 ${checked ? "border-[rgba(23,212,196,.45)] bg-[rgba(23,212,196,.08)]" : "border-[rgba(7,26,47,.08)] bg-white"}`}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() =>
                                  setLeadTagDraftIds((current) =>
                                    current.includes(tag.id) ? current.filter((id) => id !== tag.id) : [...current, tag.id],
                                  )
                                }
                                className="mt-1 size-4 rounded border-[rgba(7,26,47,.2)]"
                              />
                              <span>
                                <span className="block font-display text-xl font-bold text-[#071A2F]">{tag.tagName}</span>
                                <span className="mt-1 block text-xs font-black uppercase tracking-[0.14em] text-[#17D4C4]">{tag.tagKey}</span>
                              </span>
                            </label>
                          );
                        })
                      ) : (
                        <div className="rounded-2xl border border-dashed border-[rgba(7,26,47,.14)] bg-white p-4">
                          <p className="text-sm font-semibold leading-7 text-[#334155]">Create lead tags below, then return here to assign them to the selected lead.</p>
                        </div>
                      )}
                    </div>
                  </div>
                ) : null}

                <div className="flex flex-wrap gap-2">
                  <button type="submit" className="admin-primary-button focus-ring">
                    <Plus className="size-4" aria-hidden="true" />
                    {leadEditingId ? "Update Lead" : "Create Lead"}
                  </button>
                  {leadEditingId ? (
                    <button
                      type="button"
                      className="admin-secondary-button focus-ring"
                      onClick={() => {
                        setLeadEditingId("");
                        setLeadForm(emptyLeadForm());
                        setLeadTagDraftIds([]);
                      }}
                    >
                      Cancel edit
                    </button>
                  ) : null}
                </div>
              </form>

              <div className="mt-8 grid gap-3">
                <SectionHeading title="Lead records" subtitle="Browse every lead in the current filtered scope and open a record for editing." />
                {filteredLeads.length ? (
                  filteredLeads.map((lead) => (
                    <RecordCard
                      key={lead.id}
                      title={lead.fullName || lead.organizationName || lead.leadNumber}
                      badge={lead.status}
                      subtitle={`${lead.leadNumber} · ${lead.currentStage}`}
                      summary={lead.nextAction || "No next action recorded."}
                      meta={[
                        lead.companyId || "No company",
                        lead.workspaceId || "No workspace",
                        lead.leadOwner || "Unassigned owner",
                        lead.tagNames?.slice(0, 2).join(", ") || "No tags",
                      ]}
                      timeline={lead.timelineEvents[0]}
                      onEdit={() => handleSelectLead(lead)}
                      onArchive={async () => {
                        await archiveTruevanceSalesLead(lead.id);
                        await loadWorkspace();
                      }}
                    />
                  ))
                ) : (
                  <EmptyState title="No leads found" description="Create a lead, widen the scope filter, or clear the search term to surface records." />
                )}
              </div>

              <div className="mt-8 rounded-2xl border border-[rgba(7,26,47,.08)] bg-[rgba(245,247,250,.88)] p-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-xs font-black uppercase tracking-[0.18em] text-[#17D4C4]">Selected lead</p>
                    <h3 className="font-display text-2xl font-bold text-[#071A2F]">{selectedLead?.fullName || selectedLead?.organizationName || "Nothing selected"}</h3>
                  </div>
                  <span className="rounded-full border border-[rgba(7,26,47,.08)] px-3 py-1 text-[11px] font-black uppercase tracking-[0.14em] text-[#071A2F]">
                    {selectedLead?.leadNumber || "Lead scope"}
                  </span>
                </div>
                {selectedLead ? (
                  <div className="mt-4 grid gap-4 md:grid-cols-2">
                    <InfoLine label="Company / workspace" value={`${selectedLead.companyId || "n/a"} · ${selectedLead.workspaceId || "n/a"}`} />
                    <InfoLine label="Source / priority" value={`${selectedLead.inquirySource || "n/a"} · ${selectedLead.leadPriority || "Medium"}`} />
                    <InfoLine label="Owner / assignee" value={`${selectedLead.leadOwner || "Unassigned"} · ${selectedLead.assignedUserId || "No assigned user"}`} />
                    <InfoLine label="Team / product" value={`${selectedLead.teamAssignment || "No team"} · ${selectedLead.productFamily || "No family"} / ${selectedLead.productKey || "No key"}`} />
                  </div>
                ) : (
                  <p className="mt-3 text-sm font-semibold leading-7 text-[#334155]">Select a lead to inspect its full lifecycle, tag assignments, and timeline history.</p>
                )}
              </div>

              <div className="mt-8 rounded-2xl border border-[rgba(7,26,47,.08)] bg-[rgba(245,247,250,.88)] p-4">
                <SectionHeading title="Lead timeline" subtitle="Timeline events preserve the complete lifecycle from inquiry through conversion." />
                <div className="mt-4 grid gap-3">
                  {selectedLeadTimeline.length ? (
                    selectedLeadTimeline.map((event) => <ActivityCard key={event.id} title={event.title} detail={event.detail} badge={event.eventType} meta={[event.relatedLink || "No link", formatDisplayDate(event.createdAt)]} />)
                  ) : (
                    <EmptyState title="No timeline events yet" description="Lead activity will appear here as discovery, opportunity, and follow-up records are created." />
                  )}
                </div>
              </div>
            </article>

            <article className="grid gap-6">
              <section className="truevance-business-card truevance-business-card--dark rounded-2xl p-5 sm:p-6">
                <SectionHeading
                  title={discoveryEditingId ? "Update discovery call" : "Discovery calls"}
                  subtitle="Discovery records stay attached to the lead and inherit company and workspace scope."
                  dark
                />
                <form className="mt-6 grid gap-4" onSubmit={handleDiscoverySubmit}>
                  <Field label="Lead">
                    <select value={discoveryForm.leadId || ""} onChange={(event) => setDiscoveryForm((state) => ({ ...state, leadId: event.target.value }))} className="admin-input admin-input--dark">
                      <option value="">Select lead</option>
                      {filteredLeads.map((lead) => (
                        <option key={lead.id} value={lead.id}>
                          {lead.leadNumber} · {lead.fullName || lead.organizationName}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Opportunity">
                    <select
                      value={discoveryForm.opportunityId || ""}
                      onChange={(event) => setDiscoveryForm((state) => ({ ...state, opportunityId: event.target.value }))}
                      className="admin-input admin-input--dark"
                    >
                      <option value="">Optional opportunity</option>
                      {filteredOpportunities.map((opportunity) => (
                        <option key={opportunity.id} value={opportunity.id}>
                          {opportunity.opportunityNumber} · {opportunity.opportunityName || opportunity.leadName}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <div className="grid gap-4 md:grid-cols-2">
                    <Field label="Scheduled at">
                      <input
                        type="datetime-local"
                        value={discoveryForm.scheduledAt || ""}
                        onChange={(event) => setDiscoveryForm((state) => ({ ...state, scheduledAt: event.target.value }))}
                        className="admin-input admin-input--dark"
                      />
                    </Field>
                    <Field label="Completed at">
                      <input
                        type="datetime-local"
                        value={discoveryForm.completedAt || ""}
                        onChange={(event) => setDiscoveryForm((state) => ({ ...state, completedAt: event.target.value }))}
                        className="admin-input admin-input--dark"
                      />
                    </Field>
                    <Field label="Current stage">
                      <input value={discoveryForm.currentStage || ""} onChange={(event) => setDiscoveryForm((state) => ({ ...state, currentStage: event.target.value }))} className="admin-input admin-input--dark" />
                    </Field>
                    <Field label="Status">
                      <input value={discoveryForm.status || ""} onChange={(event) => setDiscoveryForm((state) => ({ ...state, status: event.target.value }))} className="admin-input admin-input--dark" />
                    </Field>
                    <Field label="Owner">
                      <input value={discoveryForm.assignedOwner || ""} onChange={(event) => setDiscoveryForm((state) => ({ ...state, assignedOwner: event.target.value }))} className="admin-input admin-input--dark" />
                    </Field>
                    <Field label="Lead owner">
                      <input value={discoveryForm.leadOwner || ""} onChange={(event) => setDiscoveryForm((state) => ({ ...state, leadOwner: event.target.value }))} className="admin-input admin-input--dark" />
                    </Field>
                    <Field label="Assigned user">
                      <input value={discoveryForm.assignedUserId || ""} onChange={(event) => setDiscoveryForm((state) => ({ ...state, assignedUserId: event.target.value }))} className="admin-input admin-input--dark" />
                    </Field>
                    <Field label="Team assignment">
                      <input value={discoveryForm.teamAssignment || ""} onChange={(event) => setDiscoveryForm((state) => ({ ...state, teamAssignment: event.target.value }))} className="admin-input admin-input--dark" />
                    </Field>
                    <Field label="Priority">
                      <select value={discoveryForm.leadPriority || "Medium"} onChange={(event) => setDiscoveryForm((state) => ({ ...state, leadPriority: event.target.value }))} className="admin-input admin-input--dark">
                        {["Critical", "High", "Medium", "Low"].map((priority) => (
                          <option key={priority} value={priority}>
                            {priority}
                          </option>
                        ))}
                      </select>
                    </Field>
                  </div>
                  <Field label="Outcome">
                    <input value={discoveryForm.outcome || ""} onChange={(event) => setDiscoveryForm((state) => ({ ...state, outcome: event.target.value }))} className="admin-input admin-input--dark" />
                  </Field>
                  <Field label="Notes">
                    <textarea rows={4} value={discoveryForm.notes || ""} onChange={(event) => setDiscoveryForm((state) => ({ ...state, notes: event.target.value }))} className="admin-input admin-input--dark" />
                  </Field>
                  <div className="flex flex-wrap gap-2">
                    <button type="submit" className="admin-primary-button focus-ring">
                      <CalendarDays className="size-4" aria-hidden="true" />
                      {discoveryEditingId ? "Update Discovery Call" : "Save Discovery Call"}
                    </button>
                    {discoveryEditingId ? (
                      <button
                        type="button"
                        className="admin-secondary-button focus-ring"
                        onClick={() => {
                          setDiscoveryEditingId("");
                          setDiscoveryForm(emptyDiscoveryForm());
                        }}
                      >
                        Cancel edit
                      </button>
                    ) : null}
                  </div>
                </form>
                <div className="mt-6 grid gap-3">
                  {filteredDiscoveryCalls.length ? (
                    filteredDiscoveryCalls.map((call) => (
                      <RecordCard
                        key={call.id}
                        title={call.leadName || call.discoveryCallNumber}
                        badge={call.status}
                        subtitle={`${call.discoveryCallNumber} · ${formatDisplayDate(call.scheduledAt)}`}
                        summary={call.outcome || "Discovery call ready."}
                        meta={[call.currentStage, call.leadOwner || "No owner", call.teamAssignment || "No team"]}
                        dark
                        onEdit={() => {
                          setDiscoveryEditingId(call.id);
                          setDiscoveryForm({
                            leadId: call.leadId,
                            opportunityId: call.opportunityId || "",
                            scheduledAt: call.scheduledAt,
                            completedAt: call.completedAt || "",
                            currentStage: call.currentStage,
                            status: call.status,
                            outcome: call.outcome,
                            assignedOwner: call.assignedOwner,
                            leadOwner: call.leadOwner,
                            assignedUserId: call.assignedUserId,
                            teamAssignment: call.teamAssignment,
                            leadPriority: call.leadPriority,
                            productFamily: call.productFamily,
                            productKey: call.productKey,
                            notes: call.notes,
                          });
                        }}
                        onArchive={async () => {
                          await archiveTruevanceDiscoveryCall(call.id);
                          await loadWorkspace();
                        }}
                      />
                    ))
                  ) : (
                    <EmptyState dark title="No discovery calls yet" description="Discovery records will appear here once scheduled against a lead." />
                  )}
                </div>
              </section>

              <section className="truevance-business-card truevance-business-card--light rounded-2xl p-5 sm:p-6">
                <SectionHeading
                  title={opportunityEditingId ? "Update opportunity" : "Opportunities"}
                  subtitle="Opportunities remain linked to the lead so the lifecycle stays traceable from discovery to proposal readiness."
                />
                <form className="mt-6 grid gap-4" onSubmit={handleOpportunitySubmit}>
                  <Field label="Lead">
                    <select value={opportunityForm.leadId || ""} onChange={(event) => setOpportunityForm((state) => ({ ...state, leadId: event.target.value }))} className="admin-input">
                      <option value="">Select lead</option>
                      {filteredLeads.map((lead) => (
                        <option key={lead.id} value={lead.id}>
                          {lead.leadNumber} · {lead.fullName || lead.organizationName}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <div className="grid gap-4 md:grid-cols-2">
                    <Field label="Opportunity name">
                      <input value={opportunityForm.opportunityName || ""} onChange={(event) => setOpportunityForm((state) => ({ ...state, opportunityName: event.target.value }))} className="admin-input" />
                    </Field>
                    <Field label="Opportunity type">
                      <input value={opportunityForm.opportunityType || ""} onChange={(event) => setOpportunityForm((state) => ({ ...state, opportunityType: event.target.value }))} className="admin-input" />
                    </Field>
                    <Field label="Estimated value">
                      <input type="number" value={String(opportunityForm.estimatedValue ?? 0)} onChange={(event) => setOpportunityForm((state) => ({ ...state, estimatedValue: Number(event.target.value || 0) }))} className="admin-input" />
                    </Field>
                    <Field label="Probability">
                      <input type="number" value={String(opportunityForm.probability ?? 0)} onChange={(event) => setOpportunityForm((state) => ({ ...state, probability: Number(event.target.value || 0) }))} className="admin-input" />
                    </Field>
                    <Field label="Current stage">
                      <input value={opportunityForm.currentStage || ""} onChange={(event) => setOpportunityForm((state) => ({ ...state, currentStage: event.target.value }))} className="admin-input" />
                    </Field>
                    <Field label="Status">
                      <input value={opportunityForm.status || ""} onChange={(event) => setOpportunityForm((state) => ({ ...state, status: event.target.value }))} className="admin-input" />
                    </Field>
                    <Field label="Owner">
                      <input value={opportunityForm.assignedOwner || ""} onChange={(event) => setOpportunityForm((state) => ({ ...state, assignedOwner: event.target.value }))} className="admin-input" />
                    </Field>
                    <Field label="Lead owner">
                      <input value={opportunityForm.leadOwner || ""} onChange={(event) => setOpportunityForm((state) => ({ ...state, leadOwner: event.target.value }))} className="admin-input" />
                    </Field>
                    <Field label="Assigned user">
                      <input value={opportunityForm.assignedUserId || ""} onChange={(event) => setOpportunityForm((state) => ({ ...state, assignedUserId: event.target.value }))} className="admin-input" />
                    </Field>
                    <Field label="Team assignment">
                      <input value={opportunityForm.teamAssignment || ""} onChange={(event) => setOpportunityForm((state) => ({ ...state, teamAssignment: event.target.value }))} className="admin-input" />
                    </Field>
                    <Field label="Priority">
                      <select value={opportunityForm.leadPriority || "Medium"} onChange={(event) => setOpportunityForm((state) => ({ ...state, leadPriority: event.target.value }))} className="admin-input">
                        {["Critical", "High", "Medium", "Low"].map((priority) => (
                          <option key={priority} value={priority}>
                            {priority}
                          </option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Product family">
                      <input value={opportunityForm.productFamily || ""} onChange={(event) => setOpportunityForm((state) => ({ ...state, productFamily: event.target.value }))} className="admin-input" />
                    </Field>
                    <Field label="Product key">
                      <input value={opportunityForm.productKey || ""} onChange={(event) => setOpportunityForm((state) => ({ ...state, productKey: event.target.value }))} className="admin-input" />
                    </Field>
                  </div>
                  <Field label="Next action">
                    <input value={opportunityForm.nextAction || ""} onChange={(event) => setOpportunityForm((state) => ({ ...state, nextAction: event.target.value }))} className="admin-input" />
                  </Field>
                  <Field label="Follow-up date">
                    <input type="date" value={opportunityForm.followUpDate || ""} onChange={(event) => setOpportunityForm((state) => ({ ...state, followUpDate: event.target.value }))} className="admin-input" />
                  </Field>
                  <Field label="Notes">
                    <textarea rows={4} value={opportunityForm.notes || ""} onChange={(event) => setOpportunityForm((state) => ({ ...state, notes: event.target.value }))} className="admin-input" />
                  </Field>
                  <div className="flex flex-wrap gap-2">
                    <button type="submit" className="admin-primary-button focus-ring">
                      <Target className="size-4" aria-hidden="true" />
                      {opportunityEditingId ? "Update Opportunity" : "Save Opportunity"}
                    </button>
                    {opportunityEditingId ? (
                      <button
                        type="button"
                        className="admin-secondary-button focus-ring"
                        onClick={() => {
                          setOpportunityEditingId("");
                          setOpportunityForm(emptyOpportunityForm());
                        }}
                      >
                        Cancel edit
                      </button>
                    ) : null}
                  </div>
                </form>

                <div className="mt-6 grid gap-3">
                  {filteredOpportunities.length ? (
                    filteredOpportunities.map((opportunity) => (
                      <RecordCard
                        key={opportunity.id}
                        title={opportunity.opportunityName || opportunity.opportunityNumber}
                        badge={opportunity.status}
                        subtitle={`${opportunity.opportunityNumber} · ${opportunity.currentStage}`}
                        summary={opportunity.nextAction || "No next action recorded."}
                        meta={[
                          opportunity.leadName || "No lead linked",
                          formatCurrency(opportunity.estimatedValue || 0),
                          `${opportunity.probability || 0}% win probability`,
                        ]}
                        timeline={opportunity.timelineEvents[0]}
                        onEdit={() => {
                          setOpportunityEditingId(opportunity.id);
                          setOpportunityForm({
                            leadId: opportunity.leadId,
                            opportunityName: opportunity.opportunityName,
                            opportunityType: opportunity.opportunityType,
                            estimatedValue: opportunity.estimatedValue,
                            probability: opportunity.probability,
                            currentStage: opportunity.currentStage,
                            status: opportunity.status,
                            assignedOwner: opportunity.assignedOwner,
                            leadOwner: opportunity.leadOwner,
                            assignedUserId: opportunity.assignedUserId,
                            teamAssignment: opportunity.teamAssignment,
                            leadPriority: opportunity.leadPriority,
                            productFamily: opportunity.productFamily,
                            productKey: opportunity.productKey,
                            nextAction: opportunity.nextAction,
                            followUpDate: opportunity.followUpDate,
                            notes: opportunity.notes,
                          });
                        }}
                        onArchive={async () => {
                          await archiveTruevanceSalesOpportunity(opportunity.id);
                          await loadWorkspace();
                        }}
                      />
                    ))
                  ) : (
                    <EmptyState title="No opportunities yet" description="Opportunities will appear as leads move into qualification and proposal readiness." />
                  )}
                </div>
              </section>

              <section className="truevance-business-card truevance-business-card--dark rounded-2xl p-5 sm:p-6">
                <SectionHeading
                  title={followUpEditingId ? "Update follow-up" : "Follow-ups"}
                  subtitle="Follow-up items keep the next action visible across the shared sales pipeline."
                  dark
                />
                <form className="mt-6 grid gap-4" onSubmit={handleFollowUpSubmit}>
                  <div className="grid gap-4 md:grid-cols-2">
                    <Field label="Entity type">
                      <input value={followUpForm.entityType || ""} onChange={(event) => setFollowUpForm((state) => ({ ...state, entityType: event.target.value }))} className="admin-input admin-input--dark" />
                    </Field>
                    <Field label="Entity id">
                      <input value={followUpForm.entityId || ""} onChange={(event) => setFollowUpForm((state) => ({ ...state, entityId: event.target.value }))} className="admin-input admin-input--dark" />
                    </Field>
                    <Field label="Lead">
                      <select value={followUpForm.leadId || ""} onChange={(event) => setFollowUpForm((state) => ({ ...state, leadId: event.target.value || null }))} className="admin-input admin-input--dark">
                        <option value="">Optional lead</option>
                        {filteredLeads.map((lead) => (
                          <option key={lead.id} value={lead.id}>
                            {lead.leadNumber} · {lead.fullName || lead.organizationName}
                          </option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Opportunity">
                      <select value={followUpForm.opportunityId || ""} onChange={(event) => setFollowUpForm((state) => ({ ...state, opportunityId: event.target.value || null }))} className="admin-input admin-input--dark">
                        <option value="">Optional opportunity</option>
                        {filteredOpportunities.map((opportunity) => (
                          <option key={opportunity.id} value={opportunity.id}>
                            {opportunity.opportunityNumber} · {opportunity.opportunityName || opportunity.leadName}
                          </option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Due date">
                      <input type="date" value={followUpForm.dueDate || ""} onChange={(event) => setFollowUpForm((state) => ({ ...state, dueDate: event.target.value }))} className="admin-input admin-input--dark" />
                    </Field>
                    <Field label="Priority">
                      <select value={followUpForm.priority || "Medium"} onChange={(event) => setFollowUpForm((state) => ({ ...state, priority: event.target.value }))} className="admin-input admin-input--dark">
                        {["Critical", "High", "Medium", "Low"].map((priority) => (
                          <option key={priority} value={priority}>
                            {priority}
                          </option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Status">
                      <input value={followUpForm.status || ""} onChange={(event) => setFollowUpForm((state) => ({ ...state, status: event.target.value }))} className="admin-input admin-input--dark" />
                    </Field>
                    <Field label="Lead owner">
                      <input value={followUpForm.leadOwner || ""} onChange={(event) => setFollowUpForm((state) => ({ ...state, leadOwner: event.target.value }))} className="admin-input admin-input--dark" />
                    </Field>
                    <Field label="Assigned user">
                      <input value={followUpForm.assignedUserId || ""} onChange={(event) => setFollowUpForm((state) => ({ ...state, assignedUserId: event.target.value }))} className="admin-input admin-input--dark" />
                    </Field>
                    <Field label="Team assignment">
                      <input value={followUpForm.teamAssignment || ""} onChange={(event) => setFollowUpForm((state) => ({ ...state, teamAssignment: event.target.value }))} className="admin-input admin-input--dark" />
                    </Field>
                    <Field label="Lead priority">
                      <select value={followUpForm.leadPriority || "Medium"} onChange={(event) => setFollowUpForm((state) => ({ ...state, leadPriority: event.target.value }))} className="admin-input admin-input--dark">
                        {["Critical", "High", "Medium", "Low"].map((priority) => (
                          <option key={priority} value={priority}>
                            {priority}
                          </option>
                        ))}
                      </select>
                    </Field>
                  </div>
                  <Field label="Notes">
                    <textarea rows={4} value={followUpForm.notes || ""} onChange={(event) => setFollowUpForm((state) => ({ ...state, notes: event.target.value }))} className="admin-input admin-input--dark" />
                  </Field>
                  <div className="flex flex-wrap gap-2">
                    <button type="submit" className="admin-primary-button focus-ring">
                      <ListTodo className="size-4" aria-hidden="true" />
                      {followUpEditingId ? "Update Follow-up" : "Save Follow-up"}
                    </button>
                    {followUpEditingId ? (
                      <button
                        type="button"
                        className="admin-secondary-button focus-ring"
                        onClick={() => {
                          setFollowUpEditingId("");
                          setFollowUpForm(emptyFollowUpForm());
                        }}
                      >
                        Cancel edit
                      </button>
                    ) : null}
                  </div>
                </form>

                <div className="mt-6 grid gap-3">
                  {filteredFollowUps.length ? (
                    filteredFollowUps.map((followUp) => (
                      <RecordCard
                        key={followUp.id}
                        title={followUp.followUpNumber}
                        badge={followUp.status}
                        subtitle={`${followUp.entityType} · ${followUp.priority}`}
                        summary={followUp.notes || "Follow-up item ready."}
                        meta={[followUp.entityId, formatDisplayDate(followUp.dueDate), followUp.completedAt || "Not completed"]}
                        dark
                        onEdit={() => {
                          setFollowUpEditingId(followUp.id);
                          setFollowUpForm({
                            entityType: followUp.entityType,
                            entityId: followUp.entityId,
                            leadId: followUp.leadId,
                            opportunityId: followUp.opportunityId,
                            dueDate: followUp.dueDate,
                            priority: followUp.priority,
                            status: followUp.status,
                            leadOwner: "",
                            assignedUserId: "",
                            teamAssignment: "",
                            leadPriority: "Medium",
                            notes: followUp.notes,
                            completedAt: followUp.completedAt || undefined,
                          });
                        }}
                        onArchive={async () => {
                          await archiveTruevanceSalesFollowUp(followUp.id);
                          await loadWorkspace();
                        }}
                      />
                    ))
                  ) : (
                    <EmptyState dark title="No follow-ups yet" description="Follow-up items will appear here when a next action is captured." />
                  )}
                </div>
              </section>
            </article>
          </section>

          <section className="grid gap-6 xl:grid-cols-2">
            <article className="truevance-business-card truevance-business-card--light rounded-2xl p-5 sm:p-6">
              <SectionHeading
                title={sourceEditingId ? "Update lead source" : "Lead source management"}
                subtitle="Manage the reusable source catalog that powers lead reporting, intake, and discovery automation."
              />
              <form className="mt-6 grid gap-4" onSubmit={handleSourceSubmit}>
                <div className="grid gap-4 md:grid-cols-2">
                  <Field label="Company">
                    <select value={sourceForm.companyId} onChange={(event) => setSourceForm((state) => ({ ...state, companyId: event.target.value }))} className="admin-input">
                      <option value="">Select company</option>
                      {scopeOptions.companies.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Workspace">
                    <select value={sourceForm.workspaceId} onChange={(event) => setSourceForm((state) => ({ ...state, workspaceId: event.target.value }))} className="admin-input">
                      <option value="">Select workspace</option>
                      {scopeOptions.workspaces.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Source name">
                    <input value={sourceForm.sourceName} onChange={(event) => setSourceForm((state) => ({ ...state, sourceName: event.target.value }))} className="admin-input" />
                  </Field>
                  <Field label="Source group">
                    <input value={sourceForm.sourceGroup} onChange={(event) => setSourceForm((state) => ({ ...state, sourceGroup: event.target.value }))} className="admin-input" />
                  </Field>
                </div>
                <Field label="Description">
                  <textarea rows={3} value={sourceForm.description} onChange={(event) => setSourceForm((state) => ({ ...state, description: event.target.value }))} className="admin-input" />
                </Field>
                <Field label="Sort order">
                  <input type="number" value={String(sourceForm.sortOrder)} onChange={(event) => setSourceForm((state) => ({ ...state, sortOrder: Number(event.target.value || 0) }))} className="admin-input" />
                </Field>
                <div className="flex flex-wrap gap-2">
                  <button type="submit" className="admin-primary-button focus-ring">
                    <Sparkles className="size-4" aria-hidden="true" />
                    {sourceEditingId ? "Update Source" : "Create Source"}
                  </button>
                  {sourceEditingId ? (
                    <button
                      type="button"
                      className="admin-secondary-button focus-ring"
                      onClick={() => {
                        setSourceEditingId("");
                        setSourceForm(emptySourceForm());
                      }}
                    >
                      Cancel edit
                    </button>
                  ) : null}
                </div>
              </form>
              <div className="mt-6 grid gap-3">
                {leadSourceOptions.length ? (
                  leadSourceOptions.map((source) => (
                    <CatalogCard
                      key={source.id}
                      name={source.sourceName}
                      badge={source.isActive ? "Active" : "Inactive"}
                      meta={[source.sourceGroup || "No group", source.sourceKey, `Sort ${source.sortOrder}`]}
                      description={source.description || "No description."}
                      onEdit={() => {
                        setSourceEditingId(source.id);
                        setSourceForm({
                          sourceName: source.sourceName,
                          sourceGroup: source.sourceGroup,
                          description: source.description,
                          companyId: source.companyId,
                          workspaceId: source.workspaceId,
                          sortOrder: source.sortOrder,
                        });
                      }}
                      onArchive={async () => {
                        await archiveTruevanceLeadSource(source.id);
                        await loadWorkspace();
                      }}
                    />
                  ))
                ) : (
                  <EmptyState title="No lead sources yet" description="Create source records to support source reporting and automated lead intake classification." />
                )}
              </div>
            </article>

            <article className="truevance-business-card truevance-business-card--dark rounded-2xl p-5 sm:p-6">
              <SectionHeading
                title={tagEditingId ? "Update lead tag" : "Lead tag management"}
                subtitle="Reusable tags make it easier to classify leads, assign priorities, and power future reporting."
                dark
              />
              <form className="mt-6 grid gap-4" onSubmit={handleTagSubmit}>
                <div className="grid gap-4 md:grid-cols-2">
                  <Field label="Company">
                    <select value={tagForm.companyId} onChange={(event) => setTagForm((state) => ({ ...state, companyId: event.target.value }))} className="admin-input admin-input--dark">
                      <option value="">Select company</option>
                      {scopeOptions.companies.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Workspace">
                    <select value={tagForm.workspaceId} onChange={(event) => setTagForm((state) => ({ ...state, workspaceId: event.target.value }))} className="admin-input admin-input--dark">
                      <option value="">Select workspace</option>
                      {scopeOptions.workspaces.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Tag name">
                    <input value={tagForm.tagName} onChange={(event) => setTagForm((state) => ({ ...state, tagName: event.target.value }))} className="admin-input admin-input--dark" />
                  </Field>
                  <Field label="Tag color">
                    <input value={tagForm.tagColor} onChange={(event) => setTagForm((state) => ({ ...state, tagColor: event.target.value }))} className="admin-input admin-input--dark" />
                  </Field>
                </div>
                <Field label="Description">
                  <textarea rows={3} value={tagForm.description} onChange={(event) => setTagForm((state) => ({ ...state, description: event.target.value }))} className="admin-input admin-input--dark" />
                </Field>
                <Field label="Sort order">
                  <input type="number" value={String(tagForm.sortOrder)} onChange={(event) => setTagForm((state) => ({ ...state, sortOrder: Number(event.target.value || 0) }))} className="admin-input admin-input--dark" />
                </Field>
                <div className="flex flex-wrap gap-2">
                  <button type="submit" className="admin-primary-button focus-ring">
                    <Tags className="size-4" aria-hidden="true" />
                    {tagEditingId ? "Update Tag" : "Create Tag"}
                  </button>
                  {tagEditingId ? (
                    <button
                      type="button"
                      className="admin-secondary-button focus-ring"
                      onClick={() => {
                        setTagEditingId("");
                        setTagForm(emptyTagForm());
                      }}
                    >
                      Cancel edit
                    </button>
                  ) : null}
                </div>
              </form>
              <div className="mt-6 grid gap-3">
                {leadTagOptions.length ? (
                  leadTagOptions.map((tag) => (
                    <CatalogCard
                      key={tag.id}
                      name={tag.tagName}
                      badge={tag.isActive ? "Active" : "Inactive"}
                      meta={[tag.tagKey, tag.tagColor, `Sort ${tag.sortOrder}`]}
                      description={tag.description || "No description."}
                      dark
                      onEdit={() => {
                        setTagEditingId(tag.id);
                        setTagForm({
                          tagName: tag.tagName,
                          tagColor: tag.tagColor,
                          description: tag.description,
                          companyId: tag.companyId,
                          workspaceId: tag.workspaceId,
                          sortOrder: tag.sortOrder,
                        });
                      }}
                      onArchive={async () => {
                        await archiveTruevanceLeadTag(tag.id);
                        await loadWorkspace();
                      }}
                    />
                  ))
                ) : (
                  <EmptyState dark title="No lead tags yet" description="Create tags to support segmentation, automation, and future multi-tenant reporting." />
                )}
              </div>
            </article>
          </section>

          <section className="grid gap-6 xl:grid-cols-[1fr_1fr]">
            <article className="truevance-business-card truevance-business-card--light rounded-2xl p-5 sm:p-6">
              <SectionHeading title="Status history" subtitle="Review how each lead moved across the lifecycle and what action caused the change." />
              <div className="mt-6 grid gap-3">
                {selectedLeadStatusHistory.length ? (
                  selectedLeadStatusHistory.map((history) => (
                    <HistoryCard
                      key={history.id}
                      title={`${history.fromStatus || "Start"} → ${history.toStatus}`}
                      detail={history.statusReason || history.statusAction}
                      meta={[history.statusAction, history.leadId, formatDisplayDate(history.createdAt)]}
                    />
                  ))
                ) : (
                  <EmptyState title="No status history yet" description="Status changes will appear here as leads progress through the shared lifecycle." />
                )}
              </div>
            </article>

            <article className="truevance-business-card truevance-business-card--dark rounded-2xl p-5 sm:p-6">
              <SectionHeading title="Activity history" subtitle="Activity records provide a complete audit trail for lead, opportunity, and follow-up events." dark />
              <div className="mt-6 grid gap-3">
                {selectedLeadActivities.length ? (
                  selectedLeadActivities.map((activity) => (
                    <HistoryCard
                      key={activity.id}
                      title={activity.title}
                      detail={activity.detail}
                      meta={[activity.activityType, activity.relatedType || "lead", formatDisplayDate(activity.createdAt)]}
                      dark
                    />
                  ))
                ) : (
                  <EmptyState dark title="No activity history yet" description="Activity events will be surfaced here as the shared CRM is used." />
                )}
              </div>
            </article>
          </section>

          <section className="truevance-business-card truevance-business-card--light rounded-2xl p-5 sm:p-6">
            <SectionHeading title="Navigation and system bridge" subtitle="Use these links to move between the shared CRM and the rest of Business Center OS." />
            <div className="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              {[
                { label: "Business Center Home", to: "/admin/truevance-business-center", description: "Return to the shared operating system shell.", icon: Workflow },
                { label: "Opportunity Management", to: "/admin/truevance-opportunities", description: "Review opportunity records and pipeline movement.", icon: Target },
                { label: "Client Center", to: "/admin/truevance-client-center", description: "Continue into client records and portal prep.", icon: UserRound },
                { label: "Project Center", to: "/admin/truevance-project-center", description: "Move from conversion into delivery management.", icon: Layers3 },
                { label: "Communication Center", to: "/admin/truevance-communication-center", description: "See notes, follow-ups, and timeline activity.", icon: FileText },
                { label: "Reporting Center", to: "/admin/truevance-analytics-center", description: "Review conversion and pipeline metrics.", icon: BarChart3 },
                { label: "Truevance Business Center", to: "/admin/truevance-business-center", description: "Return to the Truevance workspace overview.", icon: FileText },
                { label: "Executive Dashboard", to: "/admin/executive-dashboard", description: "Jump to the command overview surface.", icon: Clock3 },
              ].map((item) => {
                const Icon = item.icon;
                return (
                  <Link key={item.to} to={item.to} className="business-nav-card truevance-business-card focus-ring rounded-2xl p-4">
                    <span className="flex items-start justify-between gap-3">
                      <span className="grid size-11 shrink-0 place-items-center rounded-xl bg-[rgba(7,26,47,.9)] text-[#22E0D0]">
                        <Icon className="size-5" aria-hidden="true" />
                      </span>
                      <ArrowRight className="size-5 shrink-0 text-[#FF6A2B]" aria-hidden="true" />
                    </span>
                    <span className="mt-3 block font-display text-2xl font-bold text-[#071A2F]">{item.label}</span>
                    <span className="mt-2 block text-sm font-semibold leading-7 text-[#334155]">{item.description}</span>
                  </Link>
                );
              })}
            </div>
          </section>
        </div>
      </section>
    </main>
  );
}

function MetricCard({ label, value, note, tone }: { label: string; value: number | string; note: string; tone: "dark" | "light" }) {
  return (
    <article className={`truevance-business-card truevance-business-card--${tone} rounded-2xl p-5`}>
      <p className={`text-xs font-black uppercase tracking-[0.18em] ${tone === "dark" ? "text-[#22E0D0]" : "text-[#17D4C4]"}`}>{label}</p>
      <p className={`mt-2 font-display text-3xl font-bold ${tone === "dark" ? "text-[#F5F7FA]" : "text-[#071A2F]"}`}>{value}</p>
      <p className={`mt-3 text-sm font-semibold leading-7 ${tone === "dark" ? "text-[#E8EEF4]" : "text-[#334155]"}`}>{note}</p>
    </article>
  );
}

function EmptyState({ title, description, dark = false }: { title: string; description: string; dark?: boolean }) {
  return (
    <div className={`rounded-2xl border px-4 py-5 ${dark ? "border-[rgba(34,224,208,.16)] bg-[rgba(255,255,255,.04)]" : "border-[rgba(7,26,47,.08)] bg-[rgba(245,247,250,.92)]"}`}>
      <p className={`font-display text-2xl font-bold ${dark ? "text-[#F5F7FA]" : "text-[#071A2F]"}`}>{title}</p>
      <p className={`mt-2 text-sm font-semibold leading-7 ${dark ? "text-[#E8EEF4]" : "text-[#334155]"}`}>{description}</p>
    </div>
  );
}

function ScopePill({ title, value, description }: { title: string; value: number | string; description: string }) {
  return (
    <article className="truevance-business-card truevance-business-card--light rounded-2xl p-4">
      <p className="text-xs font-black uppercase tracking-[0.18em] text-[#17D4C4]">{title}</p>
      <p className="mt-2 font-display text-3xl font-bold text-[#071A2F]">{value}</p>
      <p className="mt-3 text-sm font-semibold leading-7 text-[#334155]">{description}</p>
    </article>
  );
}

function RecordCard({
  title,
  subtitle,
  summary,
  badge,
  meta,
  timeline,
  dark = false,
  onEdit,
  onArchive,
}: {
  title: string;
  subtitle: string;
  summary: string;
  badge: string;
  meta: string[];
  timeline?: { title: string };
  dark?: boolean;
  onEdit: () => void;
  onArchive: () => void;
}) {
  return (
    <article className={`rounded-2xl border p-4 ${dark ? "border-[rgba(34,224,208,.14)] bg-[rgba(255,255,255,.04)]" : "border-[rgba(7,26,47,.08)] bg-white"}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="grid gap-1">
          <p className={`font-display text-2xl font-bold ${dark ? "text-[#F5F7FA]" : "text-[#071A2F]"}`}>{title}</p>
          <p className={`text-xs font-black uppercase tracking-[0.16em] ${dark ? "text-[#22E0D0]" : "text-[#17D4C4]"}`}>{subtitle}</p>
        </div>
        <span className={`rounded-full border px-3 py-1 text-[11px] font-black uppercase tracking-[0.14em] ${dark ? "border-[rgba(34,224,208,.24)] text-[#22E0D0]" : "border-[rgba(7,26,47,.1)] text-[#071A2F]"}`}>
          {badge}
        </span>
      </div>
      <p className={`mt-3 text-sm font-semibold leading-7 ${dark ? "text-[#E8EEF4]" : "text-[#334155]"}`}>{summary}</p>
      <div className="mt-3 flex flex-wrap gap-2">
        {meta.map((item) => (
          <span key={item} className={`rounded-full px-3 py-1 text-xs font-semibold ${dark ? "bg-[rgba(7,26,47,.7)] text-[#E8EEF4]" : "bg-[rgba(34,224,208,.08)] text-[#071A2F]"}`}>
            {item}
          </span>
        ))}
      </div>
      {timeline ? (
        <p className={`mt-3 text-xs font-semibold uppercase tracking-[0.18em] ${dark ? "text-[#E8EEF4]" : "text-[#334155]"}`}>Latest: {timeline.title}</p>
      ) : null}
      <div className="mt-4 flex flex-wrap gap-2">
        <button type="button" className="admin-secondary-button focus-ring" onClick={onEdit}>
          <PencilLine className="size-4" aria-hidden="true" />
          Edit
        </button>
        <button type="button" className="admin-secondary-button focus-ring" onClick={onArchive}>
          <Trash2 className="size-4" aria-hidden="true" />
          Archive
        </button>
      </div>
    </article>
  );
}

function ActivityCard({
  title,
  detail,
  badge,
  meta,
  dark = false,
}: {
  title: string;
  detail: string;
  badge: string;
  meta: string[];
  dark?: boolean;
}) {
  return (
    <article className={`rounded-2xl border p-4 ${dark ? "border-[rgba(34,224,208,.14)] bg-[rgba(255,255,255,.04)]" : "border-[rgba(7,26,47,.08)] bg-white"}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className={`font-display text-2xl font-bold ${dark ? "text-[#F5F7FA]" : "text-[#071A2F]"}`}>{title}</p>
          <p className={`mt-1 text-xs font-black uppercase tracking-[0.16em] ${dark ? "text-[#22E0D0]" : "text-[#17D4C4]"}`}>{badge}</p>
        </div>
        <span className={`rounded-full border px-3 py-1 text-[11px] font-black uppercase tracking-[0.14em] ${dark ? "border-[rgba(34,224,208,.24)] text-[#22E0D0]" : "border-[rgba(7,26,47,.1)] text-[#071A2F]"}`}>
          Activity
        </span>
      </div>
      <p className={`mt-3 text-sm font-semibold leading-7 ${dark ? "text-[#E8EEF4]" : "text-[#334155]"}`}>{detail}</p>
      <div className="mt-3 flex flex-wrap gap-2">
        {meta.map((item) => (
          <span key={item} className={`rounded-full px-3 py-1 text-xs font-semibold ${dark ? "bg-[rgba(7,26,47,.7)] text-[#E8EEF4]" : "bg-[rgba(34,224,208,.08)] text-[#071A2F]"}`}>
            {item}
          </span>
        ))}
      </div>
    </article>
  );
}

function CatalogCard({
  name,
  badge,
  description,
  meta,
  dark = false,
  onEdit,
  onArchive,
}: {
  name: string;
  badge: string;
  description: string;
  meta: string[];
  dark?: boolean;
  onEdit: () => void;
  onArchive: () => void;
}) {
  return (
    <article className={`rounded-2xl border p-4 ${dark ? "border-[rgba(34,224,208,.14)] bg-[rgba(255,255,255,.04)]" : "border-[rgba(7,26,47,.08)] bg-white"}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className={`font-display text-2xl font-bold ${dark ? "text-[#F5F7FA]" : "text-[#071A2F]"}`}>{name}</p>
          <p className={`mt-1 text-xs font-black uppercase tracking-[0.16em] ${dark ? "text-[#22E0D0]" : "text-[#17D4C4]"}`}>{badge}</p>
        </div>
        <span className="rounded-full border border-[rgba(7,26,47,.1)] px-3 py-1 text-[11px] font-black uppercase tracking-[0.14em] text-[#071A2F]">
          Catalog
        </span>
      </div>
      <p className={`mt-3 text-sm font-semibold leading-7 ${dark ? "text-[#E8EEF4]" : "text-[#334155]"}`}>{description}</p>
      <div className="mt-3 flex flex-wrap gap-2">
        {meta.map((item) => (
          <span key={item} className={`rounded-full px-3 py-1 text-xs font-semibold ${dark ? "bg-[rgba(7,26,47,.7)] text-[#E8EEF4]" : "bg-[rgba(34,224,208,.08)] text-[#071A2F]"}`}>
            {item}
          </span>
        ))}
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        <button type="button" className="admin-secondary-button focus-ring" onClick={onEdit}>
          <PencilLine className="size-4" aria-hidden="true" />
          Edit
        </button>
        <button type="button" className="admin-secondary-button focus-ring" onClick={onArchive}>
          <Trash2 className="size-4" aria-hidden="true" />
          Archive
        </button>
      </div>
    </article>
  );
}

function HistoryCard({
  title,
  detail,
  meta,
  dark = false,
}: {
  title: string;
  detail: string;
  meta: string[];
  dark?: boolean;
}) {
  return (
    <article className={`rounded-2xl border p-4 ${dark ? "border-[rgba(34,224,208,.14)] bg-[rgba(255,255,255,.04)]" : "border-[rgba(7,26,47,.08)] bg-white"}`}>
      <div className="flex items-start justify-between gap-3">
        <p className={`font-display text-2xl font-bold ${dark ? "text-[#F5F7FA]" : "text-[#071A2F]"}`}>{title}</p>
        <span className={`rounded-full border px-3 py-1 text-[11px] font-black uppercase tracking-[0.14em] ${dark ? "border-[rgba(34,224,208,.24)] text-[#22E0D0]" : "border-[rgba(7,26,47,.1)] text-[#071A2F]"}`}>
          History
        </span>
      </div>
      <p className={`mt-3 text-sm font-semibold leading-7 ${dark ? "text-[#E8EEF4]" : "text-[#334155]"}`}>{detail}</p>
      <div className="mt-3 flex flex-wrap gap-2">
        {meta.map((item) => (
          <span key={item} className={`rounded-full px-3 py-1 text-xs font-semibold ${dark ? "bg-[rgba(7,26,47,.7)] text-[#E8EEF4]" : "bg-[rgba(34,224,208,.08)] text-[#071A2F]"}`}>
            {item}
          </span>
        ))}
      </div>
    </article>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="grid gap-2">
      <span className="text-xs font-black uppercase tracking-[0.16em] text-[#17D4C4]">{label}</span>
      {children}
    </label>
  );
}

function InfoLine({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-2xl border border-[rgba(7,26,47,.08)] bg-white px-4 py-3">
      <p className="text-xs font-black uppercase tracking-[0.16em] text-[#17D4C4]">{label}</p>
      <p className="mt-2 text-sm font-semibold leading-7 text-[#334155]">{value}</p>
    </div>
  );
}

function SectionHeading({ title, subtitle, dark = false }: { title: string; subtitle: string; dark?: boolean }) {
  return (
    <div className={`border-b pb-4 ${dark ? "border-[rgba(34,224,208,.16)]" : "border-[rgba(7,26,47,.08)]"}`}>
      <p className={`text-xs font-black uppercase tracking-[0.18em] ${dark ? "text-[#22E0D0]" : "text-[#17D4C4]"}`}>{TRUEVANCE_BRAND_NAME}</p>
      <h2 className={`mt-1 font-display text-3xl font-bold ${dark ? "text-[#F5F7FA]" : "text-[#071A2F]"}`}>{title}</h2>
      <p className={`mt-3 max-w-4xl text-sm font-semibold leading-7 ${dark ? "text-[#E8EEF4]" : "text-[#334155]"}`}>{subtitle}</p>
    </div>
  );
}

function filterLeads(leads: BusinessLeadRecord[], filters: { companyId: string; workspaceId: string; searchTerm: string }) {
  return filterByScope(leads, filters);
}

function filterByScope<T extends { companyId: string; workspaceId: string }>(items: T[], filters: { companyId: string; workspaceId: string; searchTerm: string }) {
  const search = filters.searchTerm.trim().toLowerCase();
  return items.filter((item) => {
    const matchesCompany = filters.companyId === "all" || item.companyId === filters.companyId;
    const matchesWorkspace = filters.workspaceId === "all" || item.workspaceId === filters.workspaceId;
    if (!matchesCompany || !matchesWorkspace) return false;
    if (!search) return true;
    return JSON.stringify(item).toLowerCase().includes(search);
  });
}

function buildScopeOptions(workspace: BusinessSalesWorkspacePayload | null): { companies: ScopeOption[]; workspaces: ScopeOption[] } {
  const companyMap = new Map<string, number>();
  const workspaceMap = new Map<string, number>();
  const records = [
    ...(workspace?.leads || []),
    ...(workspace?.opportunities || []),
    ...(workspace?.discoveryCalls || []),
    ...(workspace?.followUps || []),
    ...(workspace?.leadSources || []),
    ...(workspace?.leadTags || []),
    ...(workspace?.leadActivities || []),
    ...(workspace?.leadStatusHistory || []),
  ];
  for (const record of records) {
    companyMap.set(record.companyId, (companyMap.get(record.companyId) || 0) + 1);
    workspaceMap.set(record.workspaceId, (workspaceMap.get(record.workspaceId) || 0) + 1);
  }
  return {
    companies: Array.from(companyMap.entries())
      .map(([value, count]) => ({ value, count, label: formatScopeLabel(value) }))
      .sort((a, b) => a.label.localeCompare(b.label)),
    workspaces: Array.from(workspaceMap.entries())
      .map(([value, count]) => ({ value, count, label: formatScopeLabel(value) }))
      .sort((a, b) => a.label.localeCompare(b.label)),
  };
}

function formatScopeLabel(value: string) {
  if (!value) return "Unassigned";
  const normalized = value
    .replace(/^company-/, "")
    .replace(/^workspace-/, "")
    .replace(/[-_]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return normalized
    .split(" ")
    .map((part) => (part.length ? part[0].toUpperCase() + part.slice(1) : part))
    .join(" ");
}

function toDateTimeInputValue(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}
